import React, { useState } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Checkbox } from './checkbox';
import { Plus, Trash2, GripVertical, ArrowUp, ArrowDown, Copy } from 'lucide-react';

// One definition, shared with the renderer, so a new field type only has to be
// added in one place.
import type { CustomQuestionField } from './CustomQuestionsFieldset';
import FormPreview from '../admin/FormPreview';
import { validateFieldDefinitions } from '../../utils/formFieldValidation';
type CustomFormField = CustomQuestionField;

interface CustomFormFieldsManagerProps {
  fields: CustomFormField[];
  onChange: (fields: CustomFormField[]) => void;
}

/** Short, collision-resistant id that is stable for the life of the question. */
function makeFieldId(): string {
  return 'q_' + Math.random().toString(36).slice(2, 8) + Date.now().toString(36).slice(-4);
}

export default function CustomFormFieldsManager({
  fields,
  onChange
}: CustomFormFieldsManagerProps) {
  const [editingField, setEditingField] = useState<number | null>(null);

  /**
   * Adds a question at `at`, defaulting to the end of the list.
   *
   * Building a long form meant scrolling back to the top for every question,
   * so the same action is offered under each one and at the foot of the list.
   */
  const addField = (at: number = fields.length) => {
    const index = Math.max(0, Math.min(at, fields.length));
    const newField: CustomFormField = {
      fieldId: makeFieldId(),
      fieldName: '',
      fieldLabel: '',
      fieldType: 'text',
      required: false,
      options: [],
      placeholder: '',
      validation: 'none',
      // Continue the section of the question it follows, so adding a field to a
      // long form does not silently start a new group.
      section: index > 0 ? fields[index - 1].section : fields[0]?.section
    };
    const next = [...fields];
    next.splice(index, 0, newField);
    onChange(next);
    setEditingField(index);
  };

  const updateField = (index: number, field: CustomFormField) => {
    const newFields = [...fields];
    newFields[index] = field;
    onChange(newFields);
  };

  const moveField = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= fields.length) return;
    const next = [...fields];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const duplicateField = (index: number) => {
    const source = fields[index];
    const copy: CustomFormField = {
      ...source,
      // A copy is a new question: it needs its own id and its own answer key,
      // or the two would share answers.
      fieldId: makeFieldId(),
      fieldName: uniqueFieldName(source.fieldName || 'field'),
      fieldLabel: `${source.fieldLabel} (copy)`,
      options: source.options ? [...source.options] : undefined,
    };
    const next = [...fields];
    next.splice(index + 1, 0, copy);
    onChange(next);
  };

  /** Appends a numeric suffix until the key is unused. */
  const uniqueFieldName = (base: string) => {
    const taken = new Set(fields.map((f) => f.fieldName));
    let n = 2;
    let candidate = `${base}${n}`;
    while (taken.has(candidate)) candidate = `${base}${++n}`;
    return candidate;
  };

  const removeField = (index: number) => {
    const newFields = fields.filter((_, i) => i !== index);
    onChange(newFields);
    if (editingField === index) {
      setEditingField(null);
    }
  };

  const addOption = (fieldIndex: number) => {
    const field = fields[fieldIndex];
    const newOptions = [...(field.options || []), ''];
    updateField(fieldIndex, { ...field, options: newOptions });
  };

  const updateOption = (fieldIndex: number, optionIndex: number, value: string) => {
    const field = fields[fieldIndex];
    const newOptions = [...(field.options || [])];
    newOptions[optionIndex] = value;
    updateField(fieldIndex, { ...field, options: newOptions });
  };

  const removeOption = (fieldIndex: number, optionIndex: number) => {
    const field = fields[fieldIndex];
    const newOptions = (field.options || []).filter((_, i) => i !== optionIndex);
    updateField(fieldIndex, { ...field, options: newOptions });
  };

  // Capped, because the answer key is limited to 60 characters. Without this a
  // long question produced an oversized key and the whole event refused to save,
  // with the failure only appearing on submit.
  const generateFieldName = (label: string) => {
    return label
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 50)
      .replace(/_$/, '');
  };

  // Section names in the order they first appear.
  const sectionNames = fields.reduce<string[]>((acc, f) => {
    const name = f.section?.trim();
    if (name && !acc.includes(name)) acc.push(name);
    return acc;
  }, []);

  return (
    <div className="space-y-4">
      <datalist id="form-section-suggestions">
        {sectionNames.map((name) => <option key={name} value={name} />)}
      </datalist>
      <div>
        <h3 className="text-lg font-semibold">Registration questions</h3>
        {sectionNames.length > 0 && (
          <p className="text-sm text-gray-500 mt-0.5">
            {fields.length} field{fields.length === 1 ? '' : 's'} in {sectionNames.length} section
            {sectionNames.length === 1 ? '' : 's'}: {sectionNames.join(' · ')}
          </p>
        )}
      </div>

      {/* Surfaced while editing, so nothing is discovered only on save. */}
      {(() => {
        const problems = validateFieldDefinitions(fields as any);
        if (problems.length === 0) return null;
        return (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-800">
              {problems.length} thing{problems.length === 1 ? '' : 's'} to fix before this event can be saved
            </p>
            <ul className="list-disc space-y-0.5 pl-5 text-sm text-amber-900">
              {problems.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </div>
        );
      })()}

      <FormPreview fields={fields} />

      {fields.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>No questions yet.</p>
          <p className="text-sm">
            Add the questions everyone answers when they register. Paste a brief above to
            create them all at once.
          </p>
          <Button type="button" onClick={() => addField()} className="mt-4">
            <Plus className="h-4 w-4 mr-2" />
            Add the first question
          </Button>
        </div>
      )}

      {fields.map((field, index) => (
        <div key={index} className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <GripVertical className="h-4 w-4 text-gray-400" />
              <span className="font-medium">Field {index + 1}</span>
              {field.section && (
                <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                  {field.section}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => moveField(index, -1)}
                disabled={index === 0}
                title="Move up" aria-label={`Move ${field.fieldLabel || 'field'} up`}
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => moveField(index, 1)}
                disabled={index === fields.length - 1}
                title="Move down" aria-label={`Move ${field.fieldLabel || 'field'} down`}
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => duplicateField(index)}
                title="Duplicate" aria-label={`Duplicate ${field.fieldLabel || 'field'}`}
              >
                <Copy className="h-4 w-4" />
              </Button>
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => removeField(index)}
                className="text-red-600 hover:text-red-700"
                title="Delete" aria-label={`Delete ${field.fieldLabel || 'field'}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`fieldLabel-${index}`}>
                Field Label <span className="text-red-500">*</span>
              </Label>
              <Input
                id={`fieldLabel-${index}`}
                value={field.fieldLabel}
                onChange={(e) => {
                  const newField = { ...field, fieldLabel: e.target.value };
                  if (!field.fieldName) {
                    newField.fieldName = generateFieldName(e.target.value);
                  }
                  updateField(index, newField);
                }}
                placeholder="e.g., City, Profession, Experience Level"
                className={field.fieldLabel.length > 300 ? 'border-red-500' : ''}
              />
              {field.fieldLabel.length > 260 && (
                <p className={`text-xs ${field.fieldLabel.length > 300 ? 'text-red-600' : 'text-amber-600'}`}>
                  {field.fieldLabel.length} / 300 characters
                  {field.fieldLabel.length > 300 && ' — shorten this or the event will not save'}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor={`section-${index}`}>Section</Label>
              <Input
                id={`section-${index}`}
                list="form-section-suggestions"
                value={field.section || ''}
                onChange={(e) =>
                  updateField(index, { ...field, section: e.target.value || undefined })
                }
                placeholder="e.g., Personal Details — leave blank for no section"
              />
              <p className="text-xs text-gray-500">
                Fields sharing a section name are grouped under one heading on the registration form.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`helpText-${index}`}>Help text</Label>
              <Input
                id={`helpText-${index}`}
                value={field.helpText || ''}
                onChange={(e) =>
                  updateField(index, { ...field, helpText: e.target.value || undefined })
                }
                placeholder="Shown in small print beneath the question"
              />
            </div>

            {/* Lets a question stand in for what the hackathon flow would ask,
                so the answer reaches team formation instead of sitting unread. */}
            <div className="space-y-2">
              <Label htmlFor={`mapsTo-${index}`}>Use this answer for</Label>
              <select
                id={`mapsTo-${index}`}
                value={(field as any).mapsTo || ''}
                onChange={(e) =>
                  updateField(index, { ...field, mapsTo: e.target.value || undefined } as any)
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Nothing — just record the answer</option>
                <option value="skills">Their skills</option>
                <option value="lookingForTeam">Whether they want placing in a team</option>
                <option value="preferredTeamSize">Preferred team size</option>
              </select>
              <p className="text-sm text-gray-500">
                Only matters for a hackathon. Team formation reads these, so marking a question
                here lets its answer feed team matching rather than only being recorded.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`fieldName-${index}`}>
                Field Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id={`fieldName-${index}`}
                value={field.fieldName}
                onChange={(e) => updateField(index, { ...field, fieldName: e.target.value })}
                placeholder="e.g., city, profession, experience_level"
              />
              <p className="text-xs text-gray-500">
                Used internally. Should be lowercase with underscores.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor={`fieldType-${index}`}>
                Field Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={field.fieldType}
                onValueChange={(value) =>
                  updateField(index, { ...field, fieldType: value as CustomFormField['fieldType'] })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text Input</SelectItem>
                  <SelectItem value="email">Email Input</SelectItem>
                  <SelectItem value="phone">Phone Input</SelectItem>
                  <SelectItem value="textarea">Text Area</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="time">Time</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="select">Dropdown</SelectItem>
                  <SelectItem value="multiselect">Checkboxes (select all that apply)</SelectItem>
                  <SelectItem value="radio">Radio Buttons (pick one)</SelectItem>
                  <SelectItem value="checkbox">Single tick box (yes / no)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`validation-${index}`}>Validation</Label>
              <Select
                value={field.validation || 'none'}
                onValueChange={(value) =>
                  updateField(index, { ...field, validation: value as CustomFormField['validation'] })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                  <SelectItem value="url">URL</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2 pt-6">
              <Checkbox
                id={`required-${index}`}
                checked={field.required}
                onCheckedChange={(checked) =>
                  updateField(index, { ...field, required: checked as boolean })
                }
              />
              <Label htmlFor={`required-${index}`} className="text-sm font-normal">
                Required field
              </Label>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor={`placeholder-${index}`}>Placeholder Text</Label>
            <Input
              id={`placeholder-${index}`}
              value={field.placeholder || ''}
              onChange={(e) => updateField(index, { ...field, placeholder: e.target.value })}
              placeholder="e.g., Enter your city, Select your profession"
            />
          </div>

          {/* Choice lists. 'checkbox' is deliberately absent: it is a single
              yes/no tick and has no options. Use 'multiselect' for a tick list. */}
          {(field.fieldType === 'select' || field.fieldType === 'radio' || field.fieldType === 'multiselect') && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Options</Label>
                <Button type="button" onClick={() => addOption(index)} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Option
                </Button>
              </div>
              
              {(field.options || []).map((option, optionIndex) => (
                <div key={optionIndex} className="flex items-center space-x-2">
                  <Input
                    value={option}
                    onChange={(e) => updateOption(index, optionIndex, e.target.value)}
                    placeholder={`Option ${optionIndex + 1}`}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeOption(index, optionIndex)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              
              {(field.options || []).length === 0 && (
                <p className="text-sm text-gray-500">
                  Add options for the dropdown/radio buttons.
                </p>
              )}
            </div>
          )}

          {/* Only between questions: after the last one this would sit directly
              above "Add another question" and do exactly the same thing. */}
          {index < fields.length - 1 && (
            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={() => addField(index + 1)}
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4" />
                Insert a question here
              </button>
            </div>
          )}
        </div>
      ))}

      {fields.length > 0 && (
        <Button
          type="button"
          variant="outline"
          onClick={() => addField()}
          className="w-full border-dashed py-6 text-gray-600 hover:text-gray-900"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add another question
        </Button>
      )}
    </div>
  );
} 